import env from '@/config/env';
import { toPaginate } from '@/utils/infrastructure';
import { hydrateCollaboratorUsers } from '@/utils/collaborators';
import { CoreEnums } from '@/utils/domain/constants';
import { getAll, getById, create, update, remove, activate, apiRoute } from '@/services/api';

const meta = {
  page: 1,
  limit: env.DEFAULT_PAGE_SIZES[0],
  count: 0,
  totalPages: 0,
};

const Model = {
  namespace: 'apps',
  state: {
    data: [],
    pagination: meta,
    searchCriteria: '',
    cache: null,
    newApp: false,
    selectedItem: null,
    view: false,
    id: null,
    builderApp: null,
    builderContent: null,
    builderLoading: false,
    builderTab: 'preview', // 'preview' | 'code'
    // AppBuilder (metadata-drawer) state
    currentApp: null,
    // In-memory form values pending a manual save. Panels push slices into
    // this draft; the PUT is issued only when the user hits "Guardar" in the
    // builder header.
    draft: {},
    appSaving: false,

    // ---- Copy flow ----
    // visible: drawer state
    // source: the App we're copying FROM (full item from the list)
    // preview: response from app/config/migration/preview
    // previewKey: identity of the preview's (company,env) combo so the
    //   drawer can deduplicate auto-triggered calls and avoid recomputing
    //   the same preview twice
    // previewError: surfaced error message from the latest failed preview
    //   (cleared on the next successful preview)
    // previewing: loading flag for the preview call
    // copying: loading flag for the copy call
    copy: {
      visible: false,
      source: null,
      preview: null,
      previewKey: null,
      previewError: null,
      previewing: false,
      copying: false,
    },

    // Collaborator slice — backs the shared <CollaboratorsDrawer /> in
    // the app-builder. Always reads through the dedicated effects so
    // the drawer and the row-command kebab stay in sync.
    collaborators: [],
  },

  effects: {
    *fetchList({ payload }, { call, put, select }) {
      try {
        const state = yield select((s) => s.apps);
        const { limit: currentLimit } = state.pagination;
        const limit = payload?.limit || currentLimit;

        const cacheKey = `apps-${payload?.page || 1}-${limit}-${payload?.search || ''}-${
          payload?.filter || ''
        }`;

        if (state.cache && state.cache.cacheKey === cacheKey) {
          yield put({
            type: 'setApps',
            payload: {
              ...state.cache.response,
              page: payload?.page || 1,
              limit,
              cacheKey,
            },
          });
          return;
        }

        // Main management view (`/apps` sidebar menu) → `/app/manage`.
        // Per the UX guide `/manage` is reserved for top-level
        // management screens accessed from the left sidebar; every
        // other context (selectors, pickers, embedded dialogs) uses
        // `/app/allowed`. `canManage` gates configuration UI.
        const response = yield call(getAll, {
          route: 'app/manage',
          page: payload?.page || 1,
          limit,
          select:
            'id,name,description,isActive,creator,modifier,created,updated,definition,canManage',
          orderBy: payload?.orderBy || 'updated desc',
          ...(payload?.search && {
            search: payload.search,
            searchSelect: ['name', 'description'],
          }),
          ...(payload?.readPriority && { readPriority: true }),
          ...(payload?.filter && { filter: payload.filter }),
        });

        const rawItems = response.items || [];
        const items = rawItems.map((item) => {
          const raw = item.definition?.personalization?.icon;
          const icon =
            raw == null
              ? null
              : raw.documentTemporaryUrl ?? raw.downloadUrl ?? raw.documentUrl ?? null;
          return { ...item, icon };
        });

        yield put({
          type: 'setApps',
          payload: {
            ...response,
            items,
            page: payload?.page || 1,
            limit,
            cacheKey,
          },
        });
      } catch (error) {}
    },

    *search({ payload }, { put }) {
      try {
        yield put({
          type: 'setSearchCriteria',
          payload: payload.criteria,
        });
        yield put({ type: 'clearCache' });
      } catch (error) {}
    },

    *addNewApp({ payload }, { put }) {
      yield put({ type: 'addApp', payload });
    },

    *editItemApp({ payload }, { call, put }) {
      try {
        const expand = {
          ownerUser: { select: 'id,fullName' },
          collaborators: { select: 'id,fullName' },
        };
        const response = yield call(getById, {
          id: payload.id,
          route: 'app',
          expand,
          ...payload.params,
        });
        let item = response.data || response;
        const view = typeof payload.view !== 'undefined' ? payload.view : !!payload.detail;

        if (item) {
          if (item.ownerUser != null) {
            item = { ...item, owner: item.ownerUser };
          }
          if (Array.isArray(item.collaborators)) {
            item = {
              ...item,
              collaborators: item.collaborators.map((c) =>
                c && typeof c === 'object'
                  ? { id: c.id, fullName: c.fullName }
                  : { id: c, fullName: '' },
              ),
            };
          }
        }

        yield put({
          type: 'editApp',
          payload: { ...payload, view, item },
        });
      } catch (e) {
        yield put({ type: 'global/loadingItem', payload: false });
      }
    },

    *saveNewApp({ payload, params }, { call, put }) {
      try {
        const res = yield call(create, { route: 'app', data: payload });

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });

        yield put({ type: 'saveApp', payload: res });
        yield put({ type: 'clearCache' });
        yield put({
          type: 'fetchList',
          payload: { ...params, readPriority: true },
        });

        return res;
      } catch (error) {
        if (error.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
            },
            validationErrors: error.validationErrors,
          });
        }
      }
    },

    *saveEditApp({ payload, params }, { call, put }) {
      try {
        const res = yield call(update, { route: 'app', data: payload });

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });

        yield put({ type: 'saveApp', payload: res });
        yield put({ type: 'clearCache' });
        yield put({
          type: 'fetchList',
          payload: { ...params, readPriority: true },
        });
      } catch (error) {
        if (error.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
            },
            validationErrors: error.validationErrors,
          });
        }
      }
    },

    *deleteItem({ id, params }, { call, put }) {
      try {
        yield put({ type: 'global/modalConfirm', state: false, id: null });
        yield call(remove, { id, route: 'app' });

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });

        yield put({ type: 'clearCache' });
        yield put({
          type: 'fetchList',
          payload: { ...params, readPriority: true },
        });
      } catch (error) {
        yield put({ type: 'global/modalConfirm', state: false, id: null });
        if (error.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *editActive({ payload }, { call, put }) {
      try {
        yield call(activate, { id: payload.id, route: 'app', isActive: payload.isActive });
        yield put({ type: 'editActives', payload });
      } catch (e) {}
    },

    *initAppBuilder({ payload }, { call, put }) {
      const { id } = payload || {};
      if (!id) return;
      try {
        const expand = {
          ownerUser: { select: 'id,fullName' },
          collaborators: { select: 'id,fullName' },
        };
        const response = yield call(getById, { id, route: 'app', expand });
        let item = response?.data || response;
        if (item) {
          if (item.ownerUser != null) {
            item = { ...item, owner: item.ownerUser };
          }
          if (Array.isArray(item.collaborators)) {
            item = {
              ...item,
              collaborators: item.collaborators.map((c) =>
                c && typeof c === 'object'
                  ? { id: c.id, fullName: c.fullName }
                  : { id: c, fullName: '' },
              ),
            };
          }
        }
        yield put({ type: 'setCurrentApp', payload: item });
      } catch (e) {
        yield put({ type: 'setCurrentApp', payload: null });
      }
    },

    *refreshCurrentApp(_action, { call, put, select }) {
      try {
        const appId = yield select((s) => s.apps.currentApp?.id);
        if (!appId) return;
        const expand = {
          ownerUser: { select: 'id,fullName' },
          collaborators: { select: 'id,fullName' },
        };
        const response = yield call(getById, { id: appId, route: 'app', expand });
        let item = response?.data || response;
        if (item) {
          if (item.ownerUser != null) item = { ...item, owner: item.ownerUser };
          if (Array.isArray(item.collaborators)) {
            item = {
              ...item,
              collaborators: item.collaborators.map((c) =>
                c && typeof c === 'object'
                  ? { id: c.id, fullName: c.fullName }
                  : { id: c, fullName: '' },
              ),
            };
          }
        }
        yield put({ type: 'setCurrentApp', payload: item });
      } catch (e) {
        // Silent — handled elsewhere.
      }
    },

    *saveAppChanges({ payload }, { call, put, select }) {
      const current = yield select((s) => s.apps.currentApp);
      if (!current?.id) return;

      const data = { ...payload, id: current.id };
      yield put({ type: 'setAppSaving', payload: true });

      try {
        yield call(update, { route: 'app', data });

        // Re-fetch with collaborators/owner expanded so the sidebar reflects
        // names (not just GUIDs) after a save.
        const expand = {
          ownerUser: { select: 'id,fullName' },
          collaborators: { select: 'id,fullName' },
        };
        const response = yield call(getById, { id: current.id, route: 'app', expand });
        let item = response?.data || response;
        if (item) {
          if (item.ownerUser != null) item = { ...item, owner: item.ownerUser };
          if (Array.isArray(item.collaborators)) {
            item = {
              ...item,
              collaborators: item.collaborators.map((c) =>
                c && typeof c === 'object'
                  ? { id: c.id, fullName: c.fullName }
                  : { id: c, fullName: '' },
              ),
            };
          }
        }

        if (item && item.id) {
          yield put({ type: 'setCurrentApp', payload: item });
        } else {
          yield put({ type: 'setCurrentApp', payload: { ...current, ...data } });
        }

        yield put({ type: 'clearDraft' });
        yield put({ type: 'clearCache' });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
            validationErrors: error.validationErrors,
          });
        }
      } finally {
        yield put({ type: 'setAppSaving', payload: false });
      }
    },

    *loadBuilderApp({ payload }, { call, put }) {
      try {
        yield put({ type: 'setBuilderLoading', payload: true });

        const appRes = yield call(getById, { id: payload.id, route: 'app' });
        const tokenRes = yield call(apiRoute, {
          route: `app/config/generateToken/${payload.id}/${CoreEnums.AccessMode.Write}`,
          method: 'post',
        });
        const session = tokenRes?.data || tokenRes;
        let content = null;

        if (session?.versionId && session?.token) {
          try {
            const configRes = yield call(apiRoute, {
              route: `app/config/version/${payload.id}/${session.versionId}`,
              params: { token: session.token },
            });
            content = configRes?.data || configRes;
          } catch (_) {
            /* noop */
          }
        }

        yield put({
          type: 'setBuilderApp',
          payload: {
            app: appRes.data || appRes,
            content,
            builderSession: session,
          },
        });
      } catch (error) {
        yield put({ type: 'setBuilderLoading', payload: false });
      }
    },

    *cleanup(_, { put }) {
      yield put({ type: 'cleanupState' });
    },

    // ============================================
    // === COPY FLOW (preview + execute) ===
    // ============================================

    *openCopy({ payload }, { put }) {
      yield put({ type: 'setCopy', payload: { visible: true, source: payload.item } });
    },

    *closeCopy(_, { put }) {
      yield put({ type: 'resetCopy' });
    },

    /**
     * Calls POST /app/config/clone/preview to analyze clonable data
     * before executing. Stores the response so the drawer can render
     * the `permissions`, `accessControl`, `config` and `warnings` panels.
     * Backend contract: see docs/clone API.
     */
    *previewCopy({ payload }, { call, put }) {
      const { sourceAppId, sourceAppVersionId, targetCompanyId, targetEnvironmentId } =
        payload || {};
      if (!sourceAppId || !targetCompanyId || !targetEnvironmentId) return null;

      // Identity for this preview, used by the drawer to skip duplicate
      // auto-triggered calls when nothing meaningful changed.
      const previewKey = `${sourceAppId}::${targetCompanyId}::${targetEnvironmentId}`;

      yield put({
        type: 'setCopyFlag',
        payload: { previewing: true, preview: null, previewError: null, previewKey },
      });
      try {
        const res = yield call(apiRoute, {
          route: 'app/config/clone/preview',
          method: 'post',
          data: {
            sourceAppId,
            ...(sourceAppVersionId && { sourceAppVersionId }),
            targetCompanyId,
            targetEnvironmentId,
          },
        });
        const preview = res?.data || res;
        yield put({
          type: 'setCopyFlag',
          payload: { previewing: false, preview, previewError: null },
        });
        return preview;
      } catch (error) {
        const previewError = error?.message || 'preview_failed';
        // Auto-triggered previews must not spam the global notification
        // bar — the drawer surfaces the error inline with a retry CTA.
        yield put({
          type: 'setCopyFlag',
          payload: { previewing: false, previewError, previewKey: null },
        });
        return null;
      }
    },

    /**
     * Calls POST /app/config/clone/execute. On success refreshes the list
     * (so the new App is visible if landing back) and returns the response
     * with `newAppId` / `newAppVersionId` so the caller can redirect to the
     * builder. Backend contract: see docs/clone API.
     */
    *executeCopy({ payload, params }, { call, put }) {
      const {
        sourceAppId,
        sourceAppVersionId,
        targetCompanyId,
        targetEnvironmentId,
        newName,
        newDescription,
        permissionsOverride,
        accessControlOverride,
        configOverride,
      } = payload || {};
      if (!sourceAppId || !targetCompanyId || !targetEnvironmentId) return null;

      yield put({ type: 'setCopyFlag', payload: { copying: true } });
      try {
        const res = yield call(apiRoute, {
          route: 'app/config/clone/execute',
          method: 'post',
          data: {
            sourceAppId,
            ...(sourceAppVersionId && { sourceAppVersionId }),
            targetCompanyId,
            targetEnvironmentId,
            ...(newName && { newName: String(newName).trim() }),
            ...(newDescription != null && { newDescription }),
            ...(permissionsOverride && { permissionsOverride }),
            ...(accessControlOverride && { accessControlOverride }),
            ...(configOverride && { configOverride }),
          },
        });
        const result = res?.data || res;

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });

        // Refresh the list so the new app appears if the user lands back
        // here (relevant only if copy stayed in the same company/env).
        yield put({ type: 'clearCache' });
        yield put({ type: 'fetchList', payload: { ...params, readPriority: true } });

        return result;
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
        return null;
      } finally {
        yield put({ type: 'setCopyFlag', payload: { copying: false } });
      }
    },

    // ─── Collaborators (App domain) ───────────────────────────────────
    //
    // Backend contract (see "AccessControl vs Collaborator" UX guide):
    //   GET    /app/config/collaborators/{appId}
    //   POST   /app/config/collaborators/{appId}  body: { collaboratorId }
    //   DELETE /app/config/collaborators/{appId}/{collaboratorRecordId}
    //
    // Refresh contract: every mutation triggers a single inline GET
    // against the primary replica (`readPriority: true`) so the drawer
    // and the row-command kebab see the row we just POSTed/DELETEd
    // without waiting for the read replica to catch up.
    // ─────────────────────────────────────────────────────────────────

    *fetchAppCollaborators({ payload }, { call, put, select }) {
      try {
        const appId = payload?.appId || (yield select((s) => s.apps.currentApp?.id));
        if (!appId) {
          yield put({ type: 'saveAppCollaborators', payload: [] });
          return;
        }
        const res = yield call(getAll, {
          route: `app/config/collaborators/${appId}`,
          ...(payload?.readPriority && { readPriority: true }),
        });
        const raw = res?.items || res?.data?.items || res || [];
        const list = Array.isArray(raw) ? raw : [];
        // Single batched `user/paged` call keeps the GET contract
        // minimal (no $expand assumed) and works regardless of which
        // navigation property the backend uses for the join.
        const hydrated = yield call(hydrateCollaboratorUsers, list);
        yield put({ type: 'saveAppCollaborators', payload: hydrated });
      } catch (e) {
        yield put({ type: 'saveAppCollaborators', payload: [] });
      }
    },

    *addAppCollaborator({ payload }, { call, put, select }) {
      const appId = payload?.appId || (yield select((s) => s.apps.currentApp?.id));
      try {
        if (appId && payload?.collaboratorId) {
          yield call(create, {
            route: `app/config/collaborators/${appId}`,
            data: { collaboratorId: payload.collaboratorId },
          });
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'success' },
          });
        }
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors,
          });
        }
      }
      if (appId) {
        yield put({
          type: 'fetchAppCollaborators',
          payload: { appId, readPriority: true },
        });
      }
    },

    *removeAppCollaborator({ payload }, { call, put, select }) {
      const appId = payload?.appId || (yield select((s) => s.apps.currentApp?.id));
      try {
        if (appId && payload?.id) {
          yield call(apiRoute, {
            route: `app/config/collaborators/${appId}/${payload.id}`,
            method: 'delete',
          });
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'success' },
          });
        }
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      }
      if (appId) {
        yield put({
          type: 'fetchAppCollaborators',
          payload: { appId, readPriority: true },
        });
      }
    },
  },

  reducers: {
    setApps(state, { payload }) {
      return {
        ...state,
        data: payload.items || [],
        pagination: {
          ...state.pagination,
          ...toPaginate({ ...state.pagination, ...payload }),
        },
        cache: {
          response: payload,
          cacheKey: payload.cacheKey,
        },
      };
    },

    setSearchCriteria(state, { payload }) {
      return {
        ...state,
        searchCriteria: payload,
        cache: null,
      };
    },

    clearCache(state) {
      return { ...state, cache: null };
    },

    addApp(state, { payload }) {
      return {
        ...state,
        newApp: payload.state,
        selectedItem: payload.item,
        view: payload.view,
        id: payload.id,
      };
    },

    editApp(state, { payload }) {
      return {
        ...state,
        newApp: true,
        selectedItem: payload.item,
        view: payload.view,
        id: payload.id,
      };
    },

    saveApp(state) {
      return {
        ...state,
        newApp: false,
        selectedItem: null,
        view: false,
        id: null,
      };
    },

    editActives(state, { payload }) {
      return {
        ...state,
        data: state.data.map((item) =>
          item.id === payload.id ? { ...item, isActive: payload.isActive } : item,
        ),
      };
    },

    setBuilderLoading(state, { payload }) {
      return { ...state, builderLoading: payload };
    },

    setBuilderApp(state, { payload }) {
      return {
        ...state,
        builderApp: payload.app,
        builderContent: payload.content,
        builderLoading: false,
      };
    },

    setBuilderTab(state, { payload }) {
      return { ...state, builderTab: payload };
    },

    setCurrentApp(state, { payload }) {
      return { ...state, currentApp: payload };
    },

    setAppSaving(state, { payload }) {
      return { ...state, appSaving: !!payload };
    },

    updateDraft(state, { payload }) {
      if (!payload || typeof payload !== 'object') return state;
      return { ...state, draft: { ...state.draft, ...payload } };
    },

    clearDraft(state) {
      return { ...state, draft: {} };
    },

    resetAppBuilder(state) {
      return {
        ...state,
        currentApp: null,
        draft: {},
        appSaving: false,
        collaborators: [],
      };
    },

    saveAppCollaborators(state, { payload }) {
      return { ...state, collaborators: payload || [] };
    },

    setCopy(state, { payload }) {
      return { ...state, copy: { ...state.copy, ...payload } };
    },

    setCopyFlag(state, { payload }) {
      return { ...state, copy: { ...state.copy, ...payload } };
    },

    resetCopy(state) {
      return {
        ...state,
        copy: {
          visible: false,
          source: null,
          preview: null,
          previewKey: null,
          previewError: null,
          previewing: false,
          copying: false,
        },
      };
    },

    cleanupState() {
      return {
        data: [],
        pagination: meta,
        searchCriteria: '',
        cache: null,
        newApp: false,
        selectedItem: null,
        view: false,
        id: null,
        builderApp: null,
        builderContent: null,
        builderLoading: false,
        builderTab: 'preview',
        currentApp: null,
        draft: {},
        appSaving: false,
        copy: {
          visible: false,
          source: null,
          preview: null,
          previewKey: null,
          previewError: null,
          previewing: false,
          copying: false,
        },
        collaborators: [],
      };
    },
  },
};

export default Model;
